
import type { Component } from 'vue'
import { h, render, nextTick } from 'vue'
import { getAppContext } from './useAppContext'

interface ModalConfig {
  component: Component
  props?: Record<string, unknown>
  slots?: Record<string, unknown>
  isClosable?: boolean
  onClose?: () => void
  onlyOne?: boolean
}

const openModalIds = new Map<string, true>()
const modalPortals = new Map<string, { parent: Node; nextSibling: ChildNode | null }>()
let scrollLockCount = 0

function getBodyElement(): HTMLElement | null {
  return document.querySelector('body')
}

function lockBodyScroll() {
  const bodyElement = getBodyElement()
  if (!bodyElement) return
  scrollLockCount += 1
  if (scrollLockCount === 1) {
    bodyElement.style.overflow = 'hidden'
  }
}

function unlockBodyScroll() {
  const bodyElement = getBodyElement()
  if (!bodyElement) return
  scrollLockCount -= 1
  if (scrollLockCount <= 0) {
    bodyElement.style.overflow = ''
    scrollLockCount = 0
  }
}

function shouldPortalModal(modal: HTMLElement, id: string): boolean {
  const parent = modal.parentElement
  if (!parent) return false
  if (parent === document.body) return false
  if (parent.id === `contenedor-${id}`) return false
  return true
}

function portalModalToBody(modal: HTMLElement, id: string) {
  const bodyElement = getBodyElement()
  if (!bodyElement) return
  if (!shouldPortalModal(modal, id)) return
  if (modalPortals.has(id)) return

  const parent = modal.parentNode
  if (!parent) return

  const nextSibling = modal.nextSibling
  bodyElement.append(modal)
  modalPortals.set(id, { parent, nextSibling })
}

function restoreModalPortal(id: string) {
  const record = modalPortals.get(id)
  if (!record) return
  const modal = document.getElementById(id)
  if (!modal) {
    modalPortals.delete(id)
    return
  }

  const { parent, nextSibling } = record
  if (nextSibling) {
    parent.insertBefore(modal, nextSibling)
  } else {
    parent.appendChild(modal)
  }
  modalPortals.delete(id)
}

function showModalElement(modal: HTMLElement, zIndex: number) {
  modal.classList.remove('hidden')
  modal.setAttribute('tabindex', '-1')
  modal.classList.add('open', 'opened')
  modal.style.zIndex = zIndex.toString()
}

function hideModalElement(modal: HTMLElement) {
  modal.classList.remove('open')
  modal.classList.add('hidden')
  modal.setAttribute('tabindex', '')
  modal.style.zIndex = ''
}

function createBackdrop(
  id: string,
  isClosable: boolean,
  onClose: (() => void) | undefined,
  zIndex: number
) {
  const bodyElement = getBodyElement()
  if (!bodyElement) return

  const existingBackdrop = document.getElementById(`${id}-backdrop`)
  if (existingBackdrop) existingBackdrop.remove()

  const div = document.createElement('div')
  div.id = `${id}-backdrop`
  div.style.zIndex = zIndex.toString()
  div.className =
    'transition duration-300 fixed inset-0 bg-blue-900/40 dark:bg-blue-950/60 hs-overlay-backdrop backdrop-blur-sm'

  if (isClosable) {
    div.addEventListener('click', () => closeModalById(id, onClose))
  }

  bodyElement.append(div)
}

function removeBackdrop(id: string) {
  const backdrop = document.getElementById(`${id}-backdrop`)
  if (backdrop) backdrop.remove()
}

function calculateZIndexes(id: string) {
  const index = Array.from(openModalIds.keys()).indexOf(id)
  const backdropZIndex = 100 + (index * 2)
  return { backdropZIndex, modalZIndex: backdropZIndex + 1 }
}

function recalculateAllZIndexes() {
  Array.from(openModalIds.keys()).forEach((modalId, index) => {
    const modal = document.getElementById(modalId)
    const backdrop = document.getElementById(`${modalId}-backdrop`)
    if (!modal) return
    const backdropZIndex = 100 + (index * 2)
    modal.style.zIndex = (backdropZIndex + 1).toString()
    if (backdrop) {
      backdrop.style.zIndex = backdropZIndex.toString()
    }
  })
}

export function openModalById(
  id: string,
  isClosable: boolean = true,
  onClose?: () => void
) {
  const modal = document.getElementById(id)
  if (!modal) return false

  portalModalToBody(modal, id)

  // Si el modal ya estaba abierto, se reubica al final del orden para que quede siempre al frente.
  const yaAbierto = openModalIds.has(id)
  if (yaAbierto) {
    openModalIds.delete(id)
  }
  openModalIds.set(id, true)

  const { backdropZIndex, modalZIndex } = calculateZIndexes(id)
  showModalElement(modal, modalZIndex)
  if (!yaAbierto) lockBodyScroll()
  createBackdrop(id, isClosable, onClose, backdropZIndex)
  recalculateAllZIndexes()

  return true
}

export function closeModalById(id: string, onClose?: () => void) {
  const modal = document.getElementById(id)
  if (modal) {
    hideModalElement(modal)
  }

  openModalIds.delete(id)
  removeBackdrop(id)
  unlockBodyScroll()
  restoreModalPortal(id)
  recalculateAllZIndexes()

  if (onClose) onClose()
}

export function useModal() {
  const appContext = getAppContext()

  async function openModal(
    id: string,
    config: ModalConfig
  ) {
    const { component, props = {}, isClosable = true, onClose, onlyOne = true } = config
    const bodyElement = document.querySelector('body')
    if (!bodyElement) return

    let existContainer = document.getElementById(`contenedor-${id}`)
    if (!existContainer || !onlyOne) {
      if (existContainer) {
        existContainer.remove()
        render(null, existContainer)
        await nextTick()
      }
      const vnode = h(component, props || {}, config.slots || {})
      if (appContext) vnode.appContext = appContext
      if (!existContainer) {
        existContainer = document.createElement('div')
        existContainer.id = `contenedor-${id}`
      }
      render(vnode, existContainer)
      bodyElement.append(existContainer)
    }
    await nextTick()
    const modal = document.getElementById(id)
    if (modal) {
      openModalById(id, isClosable, onClose)
      return {
        closeModal: () => closeModalById(id, onClose)
      }
    }
  }

  function closeModal(id: string, onClose?: () => void) {
    closeModalById(id, onClose)
  }

  return {
    openModal,
    closeModal,
    openModalById,
    closeModalById
  }
}
